import zod from 'zod';

export function collectZodError<Output>(input: zod.ZodType<Output>) {
  return (data: unknown) => {
    const result = input.safeParse(data);

    return result.success ? null : result.error.flatten((x) => x.message).formErrors[0];
  };
}

const commons = {
  email_invalid: 'fields.email_invalid',
  email_required: 'fields.email_required',

  display_name_invalid: 'fields.display_name_invalid',
  display_name_required: 'fields.display_name_required',

  password_invalid: 'fields.password_invalid',
  password_required: 'fields.password_required',

  space_name_required: 'fields.space_name_required',
  space_name_invalid: 'fields.space_name_invalid',
};

const makeValidate =
  <Output>(input: zod.ZodType<Output>) =>
  (t: any) =>
  (val: unknown) => {
    const r = collectZodError(input)(val);

    return r ? t(r as any) : null;
  };

export const RULES = {
  email: makeValidate(
    zod
      .string()
      .min(1, {
        message: commons.email_required,
      })
      .email(commons.email_invalid)
  ),
  password: makeValidate(
    zod.string().min(1, {
      message: commons.password_required,
    })
  ),
  privacy_checked: makeValidate(
    zod.boolean().refine((bool) => bool === true, {
      message: 'fields.privacy_required',
    })
  ),
  display_name_create: makeValidate(
    zod
      .string()
      .min(1, {
        message: commons.display_name_required,
      })
      .max(30, {
        message: commons.display_name_invalid,
      })
  ),
  password_create: makeValidate(
    zod
      .string()
      .min(1, {
        message: commons.password_required,
      })
      .refine((str) => /^(?=.*\d)(?=.*[a-zA-Z])[\w~@#$%^&*+=`|{}:;!.?"()[\]-]{8,}$/.test(str), commons.password_invalid)
  ),
  space_name: makeValidate(
    zod
      .string()
      .min(1, {
        message: commons.space_name_required,
      })
      .max(30, {
        message: commons.space_name_invalid,
      })
  ),
};

/**
 * 密码策略前端可预校验的违反项(与后端 violations[] 命名一致)
 * 其余规则(90 天轮换/禁止重用/锁定等)只有服务端才可判
 */
export type PasswordPolicyViolation = 'min_length_8' | 'require_case_digit' | 'require_special';

/** 违反项 → i18n key 映射(显式列出,便于 i18n 扫描与类型收敛) */
export const PASSWORD_POLICY_VIOLATION_MESSAGE_KEYS: Record<PasswordPolicyViolation, string> = {
  min_length_8: 'password_policy.rule_min_length_8',
  require_case_digit: 'password_policy.rule_require_case_digit',
  require_special: 'password_policy.rule_require_special',
};

/** 前端预校验只关心的 3 个键(结构子集,避免依赖 apis 层的完整 PasswordRules) */
export type PasswordPolicyPrecheckRules = Partial<Record<PasswordPolicyViolation, boolean>>;

/**
 * 按策略构建密码预校验函数,返回违反项数组(空数组 = 通过)
 */
export const buildPasswordValidator =
  (rules: PasswordPolicyPrecheckRules) =>
  (value: string): PasswordPolicyViolation[] => {
    const violations: PasswordPolicyViolation[] = [];
    if (rules.min_length_8 && value.length < 8) {
      violations.push('min_length_8');
    }
    if (rules.require_case_digit && !(/[a-z]/.test(value) && /[A-Z]/.test(value) && /\d/.test(value))) {
      violations.push('require_case_digit');
    }
    if (rules.require_special && !/[^a-zA-Z0-9]/.test(value)) {
      violations.push('require_special');
    }
    return violations;
  };

/**
 * 取策略中已启用的前端可预校验规则键(用于渲染「密码需满足哪些条」提示)
 */
export const getEnabledPasswordRuleKeys = (rules: PasswordPolicyPrecheckRules): PasswordPolicyViolation[] =>
  (Object.keys(PASSWORD_POLICY_VIOLATION_MESSAGE_KEYS) as PasswordPolicyViolation[]).filter((key) =>
    Boolean(rules[key])
  );

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * 从接口错误中识别密码策略类错误码并返回 i18n 文案
 * 判别方式与后端契约一致:`typeof detail === 'object' && detail.code`
 * 非策略类错误返回 null,由调用方走既有错误处理
 */
export const getPasswordPolicyErrorMessage = (error: unknown, t: TranslateFn): string | null => {
  if (typeof error !== 'object' || error === null) return null;
  const detail = (error as { detail?: unknown }).detail;
  if (typeof detail !== 'object' || detail === null) return null;
  const { code, violations, reuse_n } = detail as { code?: unknown; violations?: unknown; reuse_n?: unknown };

  if (code === 'PASSWORD_POLICY_VIOLATION') {
    const known = Array.isArray(violations)
      ? violations.filter(
          (v): v is PasswordPolicyViolation => typeof v === 'string' && v in PASSWORD_POLICY_VIOLATION_MESSAGE_KEYS
        )
      : [];
    const rulesText = known.map((v) => t(PASSWORD_POLICY_VIOLATION_MESSAGE_KEYS[v])).join('; ');
    return t('password_policy.violation', { rules: rulesText });
  }
  if (code === 'PASSWORD_REUSED') {
    return t('password_policy.reused', { reuse_n: typeof reuse_n === 'number' ? reuse_n : '' });
  }
  return null;
};
